import {useFrame, useThree} from "@react-three/fiber";
import {useXRInputSourceState} from "@react-three/xr";
import {GetPercentFromXToY, Lerp, Range} from "js-vextensions";
import {RunInAction} from "mobx-graphlink";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import * as THREE from "three";
import {EngineConfig_VR, ImageFrameFlawType} from "../../../../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import {store} from "../../../../../Store/index.js";
import {GetControllerSide_Final} from "../../../../../Store/main/settings/vr.js";
import {ImageFrame} from "../../../../../Store/main/tools/vr/ImageFrame.js";
import {Remote_RequestPlayAudioClipForFrame, Remote_RequestTTS, Remote_RunCLVoicing, Remote_StopTTSIfLastUtteranceIdIsX} from "../../../../../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {MemoryPalaceFrame, memoryPalaceFrameDB} from "../../../../../Utils/IndexedDB/MemoryPalaceFrameDB.js";
import {NewUtteranceID} from "../../../../../Utils/Services/TTS.js";
import {GetTinyTransparentImageTexture, tinyTransparentImageAsBase64Str, useBase64ImageStrAsTexture, useVideoElementAsTexture} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {getControllerPosAndDir} from "../../../../../Utils/ThreeJS/XR/Controller.js";
import {LHQInstance} from "../../LHQ/Scene.js";
import {raycaster_main} from "../../Utils/Raycasters.js";
import {vr_memoryPalacePromptLastRequestedAt} from "../SceneXREventListeners.js";
import {MemoryPalaceSnapshotUI, MemoryPalaceSnapshotUI_count} from "./MemoryPalaceSnapshotUI.js";
import {ZUpGroupHolder} from "../../LHQ/ZUpGroup.js";
import {ProgressBar} from "./ProgressBar.js";
import {FSStatsUI} from "./FSStatsUI.js";

// debug plumbing for ImageFramesDebugProbe (remove alongside that file once the raycasting issue is resolved)
export const rayData: {
	controllerPos?: THREE.Vector3,
	controllerDir?: THREE.Vector3,
	farTarget?: THREE.Vector3,
	finalPosWorld?: THREE.Vector3,
	groupRef?: React.RefObject<THREE.Group|null>,
} = {};

export const ImageFrameUI = observer_mgl_r3((props: {
	lhq: LHQInstance,
	config: EngineConfig_VR,
	frame: ImageFrame,
})=>{
	const {lhq, config, frame} = props;
	const vrState = store.main.tools.vr;
	const {camera} = useThree();
	const vrSettings = store.main.settings.vr;
	const rightController = lhq.useVR ? useXRInputSourceState("controller", GetControllerSide_Final("right")) : null;
	const groupRef = React.useRef<THREE.Group|null>(null);

	const self = useMemo(()=>({
		thisFrameDisplayPlayedEffects: false,
		lastCLVoicingSpeechID: "",
	}), []);

	useFrame(()=>{
		/*const camWorldPos = new THREE.Vector3();
		camera.getWorldPosition(camWorldPos);*/
		const {pos: controllerPos, dir: controllerDir} = getControllerPosAndDir(rightController, GetControllerSide_Final("right"));
		rayData.controllerPos = controllerPos ?? undefined; // debug plumbing
		rayData.controllerDir = controllerDir ?? undefined;
		rayData.groupRef = groupRef;

		// update visibility
		if (groupRef.current) {
			// IF the flickering checks are active / have run within the last 1s, apply visibility from the flicker-state field 
			if (Date.now() - vrState.imageFrames_lastFlickerCheckTime <= 1000) {
				groupRef.current.visible = vrState.imageFrames_currentFlickerVisibility;
			} else {
				// else, just set it to be visible
				groupRef.current.visible = true;
			}
		}

		// update position
		if (groupRef.current && lhq.roomColliders_groupRef?.current?.children && controllerPos && controllerDir) {
			//raycaster_main.setFromCamera(new THREE.Vector2(0, 0), camera);
			raycaster_main.set(controllerPos, controllerDir); // Set up and cast the ray

			// try to find intersection with objects in the scene; if no intersection is found, place it at a fixed distance from camera
			const newPos_farTarget = new THREE.Vector3();
			const intersects = raycaster_main.intersectObjects(lhq.roomColliders_groupRef.current.children, true);
			if (intersects.length > 0) {
				newPos_farTarget.copy(intersects[0].point); // use the closest intersection point
			} else {
				// fallback: place far-target at fixed distance in front of right-controller
				const distance = 5;
				newPos_farTarget.copy(controllerPos).add(controllerDir.multiplyScalar(distance));
			}
			rayData.farTarget = newPos_farTarget.clone(); // debug plumbing

			// new-pos is a LERP from the far-target to controller (actual LERP happens at end of block)
			const frameOffset_forProgressProcessing = vrState.imageFrameOffset + (vrState.GetJumpImageFrame(config) == frame ? config.frames_duration : 0);
			const percentFromFarToClose = GetPercentFromXToY(frame.startOffset, frame.EndOffset(config), frameOffset_forProgressProcessing);
			let percentFromFarToClose_withPossibleOffset = percentFromFarToClose;
			// also scale it based on percent from far-target to controller
			let scale = percentFromFarToClose;

			const isJumpFrame = vrState.GetJumpImageFrame(config) == frame;
			const percentThroughJump = vrState.GetPercentThroughJump_WithGracePeriod();
			// if we're the jump frame (ie. active-frame + 1)...
			if (isJumpFrame) {
				// ...and that "jump" is active, then add a multiplicative scaling effect (0% at jump start, 100% at jump end)
				if (percentThroughJump != null) {
					scale *= Lerp(0, 1, percentThroughJump);
					// also add a slight offset to the position, so the jump-frame appears "in front of" the existing active-frame
					percentFromFarToClose_withPossibleOffset += .001;
				}
				// else, the jump is not active; user must have just "scrolled backward" into previous frame, so make this frame "invisible" temporarily by scaling to 0% (this is needed for only a split second, until ImageFrames unmounts this comp)
				else {
					scale = 0;
				}
			}

			const newPos = new THREE.Vector3().lerpVectors(newPos_farTarget, controllerPos, percentFromFarToClose_withPossibleOffset);
			rayData.finalPosWorld = newPos.clone(); // debug plumbing
			// newPos is in world/root space (y-up), but this group lives inside the ZUpGroup (rotated) -- convert to local before applying
			groupRef.current.position.copy(ZUpGroupHolder.WorldPosToLocal(newPos));
			groupRef.current.scale.set(scale, scale, scale);
		}

		// update rotation to face the right-controller
		if (groupRef.current && camera && controllerPos) {
			// controllerPos is in world/root space (y-up); compute the desired world-space orientation, then convert it
			// into the ZUpGroup's local space (via ZUpGroupHolder, instead of parent-traversal)
			groupRef.current.updateWorldMatrix(true, false);
			const groupWorldPos = new THREE.Vector3();
			groupRef.current.getWorldPosition(groupWorldPos);
			// note: Matrix4.lookAt(eye, target, up) points the rotation's -z toward "target" (camera-convention); Object3D.lookAt
			// does the same only for cameras/lights, and flips it for regular objects so their +z faces the target. Since this
			// quad's visible face is its +z (PlaneGeometry), we swap eye/target here to mimic Object3D.lookAt's object behavior.
			const rotMatrix_world = new THREE.Matrix4().lookAt(controllerPos, groupWorldPos, new THREE.Vector3(0, 1, 0)); // y-up-root "up", so quads stay flat to the floor
			const quat_world = new THREE.Quaternion().setFromRotationMatrix(rotMatrix_world);
			groupRef.current.quaternion.copy(ZUpGroupHolder.WorldQuatToLocal(quat_world));
		}
	});

	//const voiceSound = config.frames_voicvrState.snapshotRevealLevele_soundTag ? GetSounds_WithUserTag(config.frames_voice_soundTag)[0] : null;

	// if this frame just became the "focus frame", play its effects
	useFrame(()=>{
		const isFocusFrame = vrState.imageFrameOffset >= frame.startOffset && vrState.imageFrameOffset < frame.EndOffset(config);
		const isJumpFrame = vrState.GetJumpImageFrame(config) == frame && vrState.GetPercentThroughJump_WithGracePeriod() != null;
		const isFocusOrJumpFrame = isFocusFrame || isJumpFrame;

		const framePool = config.frames_pools.find(a=>a.name == frame.poolName);
		const isPlayable = !!(framePool && (frame.texturePath || frame.videoPath) && (frame.frameData_textureStr || frame.frameData_videoClip?.blob));
		//if (isFocusOrJumpFrame && isPlayable && !vrState.visibleFramesAtTimeOfLastFlawCheckerPlay.includes(frame)) {

		const effectsShouldBePlayingNow = isFocusOrJumpFrame && isPlayable && vrState.right_interactionMode == "frames";

		// if frame should have effects played, but they haven't been started yet (during this frame-display), then play them now
		if (effectsShouldBePlayingNow && !self.thisFrameDisplayPlayedEffects) {
			self.thisFrameDisplayPlayedEffects = true;
			RunInAction("ImageFrameUI_useEffect", ()=>{
				//vrState.visibleFramesAtTimeOfLastFlawCheckerPlay = [vrState.GetActiveImageFrame(config), vrState.GetJumpImageFrame(config)].filter(a=>a != null) as ImageFrame[];
				frame.effectsPlayed = true;
			});

			(async()=>{
				// for certain flaw-types, there are effects that just always play (ie. no user checkbox for it); do those here
				if (framePool.flaw_type == ImageFrameFlawType.wrongNameVoiced) {
					const charToVoice_initials = frame.lie ? frame.lie_initialsOfLieChar : frame.targetInitials;
					const char = framePool.characters?.find(a=>a.initials == charToVoice_initials);
					const charNameToVoice = char?.name_voicing ?? "Unknown character";
					await Remote_RequestTTS(charNameToVoice, config.frames_voice_volumeMultiplier ?? 1, config.frames_voice_speedMultiplier ?? 1);
				}

				// play audio-clip effect, if enabled
				if (framePool?.audioClip_enabled && frame.texturePath) { // frame-image must exist, else we don't know what series the random audio-clip should be pulled from
					const playClipFromAnotherEpisode = frame.lie && framePool.flaw_type == ImageFrameFlawType.wrongAudioClipPlayed;
					await Remote_RequestPlayAudioClipForFrame(frame.poolName!, frame.texturePath, playClipFromAnotherEpisode);
				}

				// play video-clip effect, if enabled, and video-data is available (data could be missing here [despite earlier non-null check], in case videoClip.Cleanup() was called in-between the two checks)
				if (framePool?.videoClip_enabled && frame.frameData_videoClip?.blob != null) {
					//Log_Direct("Trying to play video-clip effect (TODO):", frame.frameData_videoClip); // LOGGING CLIP IS MEMORY LEAK!
					const videoUrl = URL.createObjectURL(frame.frameData_videoClip.blob);
					const videoElement = document.createElement("video");
					videoElement.src = videoUrl;
					videoElement.autoplay = true;
					videoElement.volume = framePool.videoClip_volumeMultiplier ?? 1;

					// Remove element and clean up when playback ends
					const cleanup = ()=>{
						URL.revokeObjectURL(videoUrl);
						// removal of the video element itself (from the document, and from the frames-array/store) occurs in ImageFrame.Cleanup
					};
					videoElement.addEventListener("abort", cleanup);
					videoElement.addEventListener("error", cleanup);
					videoElement.addEventListener("ended", cleanup);
					// in case we "stop" the video (as seen in else-block below), we also want to do the cleanup
					//videoElement.addEventListener("emptied", cleanup);

					document.querySelector("#hidden_early")!.appendChild(videoElement);

					RunInAction("ImageFrameUI_useEffect.playVideoClip", ()=>{
						frame.frameData_videoClip_player = videoElement;
					});
				}

				// play dream-clip effect, if enabled
				if (framePool.dreamClip_enabled) { //&& firstTimePlayFlawCheckerForFrame)
					const playDreamClip_config = {
						delay: 0,
						volumeMultiplier: vrSettings.conceptLink_ttsVolumeMultiplier,
						utteranceId: NewUtteranceID(Math.random().toString()), // we don't know the text that'll be voiced, so just use random string as utterance-id
					};
					self.lastCLVoicingSpeechID = playDreamClip_config.utteranceId;
					await Remote_RunCLVoicing(playDreamClip_config);
				}
			})();
		}

		// else, if no effects should be playing now, but we started playing effects earlier (during this frame-display), then attempt to stop any effects that were started earlier for this frame
		else if (!effectsShouldBePlayingNow && self.thisFrameDisplayPlayedEffects) {
			PausePlayingEffects();
		}
	});

	const PausePlayingEffects = useCallback(()=>{
		if (frame.frameData_videoClip_player) {
			/*frame.frameData_videoClip_player.src = "";
				frame.frameData_videoClip_player.load();*/
			// pausing is sufficient; we have regular life-cycle handlers above (and in ImageFrame.Cleanup) to remove the video-element, etc., when we're confident it's safe to
			frame.frameData_videoClip_player.pause();
		}

		const framePool = config.frames_pools.find(a=>a.name == frame.poolName);
		if (framePool?.dreamClip_enabled && self.lastCLVoicingSpeechID) {
			Remote_StopTTSIfLastUtteranceIdIsX(self.lastCLVoicingSpeechID);
		}
	}, [frame, config, self]);
	// on unmount, always pause any playing effects (eg. video-clip); safe since ImageFramesUI only removes ImageFrameUI instances when they're no longer relevant for display/effects
	useEffect(()=>{
		return ()=>PausePlayingEffects();
	}, [PausePlayingEffects]);

	const [texWToHRatio, setTexWToHRatio] = useState(16/9);
	const imageTexture = useBase64ImageStrAsTexture(frame.frameData_textureStr ?? tinyTransparentImageAsBase64Str, GetTinyTransparentImageTexture().image, useCallback(img=>{
		if (img.src != tinyTransparentImageAsBase64Str) {
			setTexWToHRatio(img.width / img.height);
		}
	}, []));

	// if video-clip was selected, and a player was created for it, use that as the texture instead
	const videoTexture = useVideoElementAsTexture(frame.frameData_videoClip_player);

	const snapshotsForMemoryPalaceTarget = frame.currentMemoryPromptInfo
		? memoryPalaceFrameDB.GetFramesForTarget_MobX(frame.currentMemoryPromptInfo.target)
			// exclude entries stored after memoryPalacePromptLastRequestedAt (as then it's probably the one we're storing now for this frame-viewing)
			.filter(a=>a.createdAt < vr_memoryPalacePromptLastRequestedAt)
			// but grab the previous X, to fill the snapshot-ui slots
			.slice(-MemoryPalaceSnapshotUI_count)
		: [];

	//if (vrState.right_interactionMode != "frames") return null; // uncomment this (and comment check in ImageFrames), for "frozen but preserved effect" (on mode leave-then-back) instead of effect-restart
	return (
		<group ref={groupRef} position={[0, 0, 0]} rotation={[0, 0, 0]} scale={[0, 0, 0]}>
			<ProgressBar texWToHRatio={texWToHRatio}/>
			{snapshotsForMemoryPalaceTarget.length > 0 && Range(0, MemoryPalaceSnapshotUI_count - 1, 1).map(index=>{
				const miniFrame = snapshotsForMemoryPalaceTarget[index] as MemoryPalaceFrame|n;
				return <MemoryPalaceSnapshotUI key={index} mainTexWToHRatio={texWToHRatio} snapshotImageData={miniFrame?.imageData ?? tinyTransparentImageAsBase64Str} index={index}/>;
			})}
			<mesh>
				<planeGeometry args={[texWToHRatio, 1]} />
				<meshBasicMaterial map={videoTexture ?? imageTexture}
					// needed for `videoTexture.colorSpace = THREE.SRGBColorSpace` (in 3JSHooks.ts) to take effect in every case
					onUpdate={a=>a.needsUpdate = true}/>
			</mesh>
			{vrSettings.stats_show && <FSStatsUI mainTexWToHRatio={texWToHRatio}/>}
		</group>
	);
});